import React, { useState } from 'react';
import { XMarkIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';

interface SchedulingModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (date: string) => void;
    currentDate?: string;
    minDate?: string;
    maxDate?: string;
}

const SchedulingModal: React.FC<SchedulingModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    currentDate,
    minDate,
    maxDate
}) => {
    const { actualTheme } = useTheme();
    const [selectedDate, setSelectedDate] = useState(() => {
        const d = currentDate || new Date().toISOString().split('T')[0];
        return d.includes('T') ? d.split('T')[0] : d;
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            <div className={`relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'
                }`}>
                {/* Header */}
                <div className={`flex items-center justify-between p-6 border-b ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
                    }`}>
                    <h3 className={`text-xl font-bold flex items-center gap-2 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
                        }`}>
                        <CalendarIcon className="h-6 w-6 text-primary-500" />
                        Schedule Safari
                    </h3>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-lg transition-colors ${actualTheme === 'dark' ? 'text-slate-400 hover:bg-slate-700' : 'text-neutral-500 hover:bg-neutral-100'
                            }`}
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-8">
                    <p className={`text-sm mb-6 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                        }`}>
                        Select a start date for your custom safari. All transfers and tours will be scheduled sequentially from this date.
                    </p>

                    <div className="space-y-4">
                        <div>
                            <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                                }`}>
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                min={minDate || new Date().toISOString().split('T')[0]}
                                max={maxDate}
                                className={`w-full px-4 py-3 rounded-xl border font-medium focus:ring-2 focus:ring-primary-500 outline-none transition-all ${actualTheme === 'dark'
                                    ? 'bg-slate-700 border-slate-600 text-white'
                                    : 'bg-neutral-50 border-neutral-200 text-primary-900'
                                    }`}
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className={`p-6 border-t flex gap-3 ${actualTheme === 'dark' ? 'border-slate-700 bg-slate-900/30' : 'border-neutral-200 bg-neutral-50'
                    }`}>
                    <button
                        onClick={onClose}
                        className={`flex-1 px-4 py-3 rounded-xl font-bold transition-all ${actualTheme === 'dark'
                            ? 'bg-slate-700 text-white hover:bg-slate-600'
                            : 'bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50'
                            }`}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onConfirm(selectedDate)}
                        className="flex-1 px-4 py-3 bg-primary-800 text-white font-bold rounded-xl hover:bg-primary-700 transition-all shadow-lg shadow-primary-900/20"
                    >
                        Save Schedule
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SchedulingModal;
